import { sceneManager } from '../script.js';
import magneticVertex from '../../../shaders/magnetic/vertexMagneticTangled.glsl';
import magneticFragment from '../../../shaders/magnetic/fragmentMagneticTangled.glsl';

import {
  Vector3,
  BufferGeometry,
  ShaderMaterial,
  AdditiveBlending,
  Group,
  Line,
  MathUtils,
  BufferAttribute
} from 'three';

const PARAMS = {
  elemRadiusFactor: 1.01,
  stepSizeFactor: 0.1,
  numSteps: 200,
  numSeeds: 250,
  fieldUpdateInterval: 0.5,
  numPolesPerType: 20,
  jitterAmplitudeFrac: 0.15,
  jitterFreqMin: 0.4,
  jitterFreqMax: 0.3
};

function createLineShaderMaterial() {
  return new ShaderMaterial({
    vertexShader: magneticVertex,
    fragmentShader: magneticFragment,
    transparent: true,
    blending: AdditiveBlending,
    depthTest: true,
    depthWrite: false,
  });
}

export function initMagneticField(elemObject) {
  const R = elemObject.size * PARAMS.elemRadiusFactor;
  const computed = {
    stepSize: R * PARAMS.stepSizeFactor,
    effectivePoleThreshold: R * 0.05,
    epsilon: R * 1e-3,
    clampConstant: Math.pow(R * 0.01, 2),
    jitterAmplitude: R * PARAMS.jitterAmplitudeFrac
  };

  const fieldLinesGroup = new Group();
  elemObject.mesh.add(fieldLinesGroup);

  class MagneticPole {
    constructor(pos, strength) {
      this.basePos = pos.clone().normalize().multiplyScalar(R);
      this.position = this.basePos.clone();
      this.strength = strength;
      this.time = 0;
      this.freq = new Vector3(
        MathUtils.randFloat(PARAMS.jitterFreqMin, PARAMS.jitterFreqMax) * 2 * Math.PI,
        MathUtils.randFloat(PARAMS.jitterFreqMin, PARAMS.jitterFreqMax) * 2 * Math.PI,
        MathUtils.randFloat(PARAMS.jitterFreqMin, PARAMS.jitterFreqMax) * 2 * Math.PI
      );
      this.phase = new Vector3(
        MathUtils.randFloat(0, 2 * Math.PI),
        MathUtils.randFloat(0, 2 * Math.PI),
        MathUtils.randFloat(0, 2 * Math.PI)
      );
    }
    update(dt) {
      this.time += dt;
      const amp = computed.jitterAmplitude;
      const raw = new Vector3(
        Math.sin(this.freq.x * this.time + this.phase.x),
        Math.sin(this.freq.y * this.time + this.phase.y),
        Math.sin(this.freq.z * this.time + this.phase.z)
      ).normalize().multiplyScalar(amp);
      const n = this.basePos.clone().normalize();
      const tangent = raw.sub(n.multiplyScalar(raw.dot(n)));
      this.position.copy(this.basePos).add(tangent);
    }
  }

  const allPoles = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  function makePoles(strength) {
    for (let i = 0; i < PARAMS.numPolesPerType; i++) {
      const y = 1 - (2 * i) / (PARAMS.numPolesPerType - 1);
      const r = Math.sqrt(1 - y * y);
      const theta = golden * i;
      const pos = new Vector3(
        r * Math.cos(theta),
        y * Math.sign(strength),
        r * Math.sin(theta)
      ).multiplyScalar(R);
      allPoles.push(new MagneticPole(pos, strength));
    }
  }
  makePoles(1);
  makePoles(-1);

  function computeB(p) {
    const B = new Vector3(), k = 1.5;
    allPoles.forEach(pole => {
      const r = new Vector3().subVectors(p, pole.position);
      const d = r.length();
      if (d < computed.epsilon) return;
      B.add(r.normalize().multiplyScalar((k * pole.strength) / (d * d + computed.clampConstant)));
    });
    return B;
  }

  function generateSeeds(n, signOffset = 0) {
    const arr = [];
    for (let i = 0; i < n; i++) {
      const t = (i + signOffset) / (n - 1 + signOffset);
      const phi = golden * (i + signOffset);
      const y = 1 - 2 * t;
      const r = Math.sqrt(1 - y * y);
      arr.push(new Vector3(
        r * Math.cos(phi),
        y,
        r * Math.sin(phi)
      ).multiplyScalar(R));
    }
    return arr;
  }

  const half = Math.floor(PARAMS.numSeeds / 2);
  const dynamicSeeds = generateSeeds(half, 0);
  const fixedSeeds   = generateSeeds(PARAMS.numSeeds, 0);

  const maxPoints = PARAMS.numSteps * 1.5;
  const dynamicLines = [];

  function traceLine(seed) {
    const pts = [];
    const trace = (p0, dir) => {
      let cur = p0.clone();
      pts.push(cur.clone());
      for (let i = 0; i < PARAMS.numSteps; i++) {
        if (allPoles.some(p => cur.distanceTo(p.position) < computed.effectivePoleThreshold)) break;
        const B = computeB(cur);
        if (!B.length()) break;
        cur = cur.add(B.normalize().multiplyScalar(dir * computed.stepSize));
        if (cur.length() > R * 2) break;
        pts.push(cur.clone());
      }
    };
    trace(seed, 1);
    const rev = pts.slice().reverse();
    pts.length = 0;
    trace(seed, -1);
    return rev.concat(pts.slice(1));
  }

  function createTraceLines(seeds, dynamic) {
    seeds.forEach(seed => {
      const geo = new BufferGeometry();
      geo.setAttribute('position', new BufferAttribute(new Float32Array(maxPoints * 3), 3));
      const line = new Line(geo, createLineShaderMaterial());
      fieldLinesGroup.add(line);
      dynamicLines.push({ line, seed, isDynamicSeed: dynamic });
    });
  }

  createTraceLines(dynamicSeeds, true);
  createTraceLines(fixedSeeds, false);

  let lastT = performance.now() / 1000;
  return {
    fieldLinesGroup,
    update(dt) {
      allPoles.forEach(p => p.update(dt));
      fieldLinesGroup.visible = sceneManager.commands.controls.magnetosphere;
      const now = performance.now() / 100;
      if (now - lastT < PARAMS.fieldUpdateInterval) return;

      dynamicLines.forEach(obj => {
        const pts = traceLine(obj.seed);
        const cnt = pts.length;
        const posArr = obj.line.geometry.attributes.position.array;
        for (let j = 0; j < cnt; j++) {
          const v = pts[j];
          posArr[j*3]   = v.x;
          posArr[j*3+1] = v.y;
          posArr[j*3+2] = v.z;
        }
        obj.line.geometry.attributes.position.needsUpdate = true;
        obj.line.geometry.setDrawRange(0, cnt);
        if (obj.isDynamicSeed) {
          obj.seed = pts[Math.floor(cnt / 2)];
        }
      });
      lastT = now;
    }
  };
}

export function updateMagnetic(magneticField, speed) {
  const limSpeed = Math.min(Math.abs(speed*1e-1), 1e-3);
  magneticField.update(limSpeed);
}
